import React, { useEffect, useRef, useState } from 'react'
import get from 'lodash/get'
import useSWR from 'swr'
import styled from 'styled-components'
import BackgroundImage from '../../components/BackgroundImage'
import { fetcher, getVimeoEmbedUrl, getVimeoMeta } from '../../utils/helpers'

const StyledVideoPlayer = styled.div`
  position: relative;
  overflow: hidden;

  .video-frame {
    background: #101011;
    position: relative;
    overflow: hidden;
    padding-top: ${(props) => props.aspectRatio}%;
  }

  video,
  iframe {
    border: 0;
    display: block;
    height: 100%;
    left: 0;
    object-fit: cover;
    pointer-events: auto;
    position: absolute;
    top: 0;
    width: 100%;
  }

  .primary-video {
    z-index: 1;
  }

  .primary-video.is-waiting-for-play {
    opacity: 0;
    pointer-events: none;
  }

  .primary-video.is-looping-video {
    opacity: 1;
    pointer-events: auto;
    z-index: 2;
  }

  button {
    border: 0;
    background: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    outline: none;
  }

  .poster-wrapper,
  .player-overlay {
    cursor: pointer;
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
  }

  .poster-wrapper {
    z-index: 2;
  }

  .poster-wrapper.is-looping-underlay {
    cursor: default;
    pointer-events: none;
    z-index: 1;
  }

  .poster-image,
  .poster-video {
    height: 100%;
    left: 0;
    object-fit: cover;
    position: absolute;
    top: 0;
    width: 100%;
  }

  .poster-image {
    z-index: 1;
  }

  .poster-video {
    opacity: 0;
    transition: opacity 0.25s ease;
    z-index: 2;
  }

  .poster-video.is-ready {
    opacity: 1;
  }

  .player-overlay {
    z-index: 3;
  }

  .player-play-icon {
    text-indent: -9999px;
    background-image: url('/images/play-white.svg');
    background-position: center center;
    background-repeat: no-repeat;
    background-size: contain;
    cursor: pointer;
    display: block;
    height: 2rem;
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 2rem;
  }

  .player-fullscreen {
    text-indent: -9999px;
    background: url(/images/fullscreen.svg) center center no-repeat;
    background-size: contain;
    bottom: 1rem;
    cursor: pointer;
    display: block;
    height: 22px;
    opacity: 0;
    position: absolute;
    right: 1rem;
    visibility: hidden;
    width: 20px;
    z-index: 6;
  }

  .player-play-pause {
    text-indent: -9999px;
    background: url(/images/play-white.svg) center center no-repeat;
    background-size: contain;
    bottom: 1rem;
    cursor: pointer;
    display: block;
    height: 20px;
    left: 1rem;
    opacity: 0;
    position: absolute;
    visibility: hidden;
    width: 20px;
    z-index: 6;
  }

  .player-play-pause.is-playing-true {
    background: url(/images/pause.svg) center center no-repeat;
    background-size: contain;
    height: 18px;
    width: 18px;
  }

  .player-progress {
    opacity: 0;
    visibility: hidden;
    z-index: 6;
  }

  .player-progress input[type='range'] {
    -webkit-appearance: none;
    background-color: #ffffff;
    border: 0;
    border-radius: 0;
    bottom: 0;
    height: 2px;
    left: 0;
    outline: none;
    overflow: hidden;
    position: absolute;
    width: 100%;
  }

  input[type='range']::-webkit-slider-thumb {
    -webkit-appearance: none;
    background: ${(props) => props.playerTheme};
    cursor: ew-resize;
    height: 2px;
    width: 12px;
  }

  input[type='range']::-moz-range-thumb {
    -moz-appearance: none;
    background: ${(props) => props.playerTheme};
    border: 0;
    border-radius: 0;
    height: 2px;
    width: 12px;
  }

  &.has-played-true {
    .poster-wrapper:not(.is-looping-underlay),
    .player-overlay {
      display: none;
    }

    .player-play-pause,
    .player-fullscreen,
    .player-progress {
      opacity: 1;
      visibility: visible;
      transition: all 0.3s;
    }
  }

`

const getPosterAspectRatio = (poster) => {
  const width = Number(get(poster, 'dimensions.width') || get(poster, 'width'))
  const height = Number(get(poster, 'dimensions.height') || get(poster, 'height'))

  if (!width || !height) return null
  return (height / width) * 100
}

const getIsMobileViewport = () =>
  typeof window !== 'undefined' &&
  !!window.matchMedia &&
  window.matchMedia('(max-width: 767px)').matches

function VideoPlayer({
  videoId,
  videoUrl,
  mobileVideoUrl,
  posterVideoUrl,
  loop = false,
  allowMobileLoop = false,
  playerTheme = 'blue',
  poster,
}) {
  const playerRef = useRef(null)
  const videoRef = useRef(null)
  const posterVideoRef = useRef(null)
  const posterAspectRatio = getPosterAspectRatio(poster)
  const lockAspectToPoster = loop && !!posterAspectRatio
  const [isMobile, setIsMobile] = useState(getIsMobileViewport)
  const autoLoop = loop && (!isMobile || allowMobileLoop)
  const effectiveVideoUrl = isMobile && mobileVideoUrl ? mobileVideoUrl : videoUrl
  const [hasPlayed, setHasPlayed] = useState(autoLoop)
  const [playing, setPlaying] = useState(autoLoop)
  const [played, setPlayed] = useState(0)
  const [aspectRatio, setAspectRatio] = useState(
    lockAspectToPoster ? posterAspectRatio : 56.25,
  )
  const [loadError, setLoadError] = useState(false)
  const [retryToken, setRetryToken] = useState(0)
  const [videoReady, setVideoReady] = useState(false)
  const [posterVideoReady, setPosterVideoReady] = useState(false)
  const [isNearViewport, setIsNearViewport] = useState(
    () => !getIsMobileViewport(),
  )

  const vimeoUrl = getVimeoEmbedUrl(videoId, {
    autoplay: autoLoop ? 1 : undefined,
    autopause: 0,
    background: autoLoop ? 1 : undefined,
    byline: 0,
    controls: autoLoop ? 0 : 1,
    dnt: 1,
    loop: autoLoop ? 1 : undefined,
    muted: autoLoop ? 1 : undefined,
    playsinline: 1,
    portrait: 0,
    title: 0,
  })

  const { data } = useSWR(!videoUrl && videoId ? getVimeoMeta(videoId) : null, fetcher)
  const fallbackAspectRatio =
    data?.height && data?.width ? (data.height / data.width) * 100 : aspectRatio
  const posterUrl = get(poster, 'url', '')

  useEffect(() => {
    setLoadError(false)
    setVideoReady(false)
    setPosterVideoReady(false)
    setPlayed(0)
    setAspectRatio(lockAspectToPoster ? posterAspectRatio : 56.25)

    if (!autoLoop) {
      setHasPlayed(false)
      setPlaying(false)
    } else {
      setHasPlayed(true)
      setPlaying(true)
    }
  }, [
    effectiveVideoUrl,
    videoId,
    posterVideoUrl,
    autoLoop,
    lockAspectToPoster,
    posterAspectRatio,
  ])

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined

    const query = window.matchMedia('(max-width: 767px)')
    const update = () => setIsMobile(query.matches)
    update()

    if (query.addEventListener) {
      query.addEventListener('change', update)
      return () => query.removeEventListener('change', update)
    }

    query.addListener(update)
    return () => query.removeListener(update)
  }, [])

  const playSilently = (video) => {
    if (!video) return

    video.defaultMuted = true
    video.muted = true
    video.playsInline = true

    const play = video.play()
    if (play && typeof play.catch === 'function') play.catch(() => {})
  }

  useEffect(() => {
    if (!isMobile) {
      setIsNearViewport(true)
      return undefined
    }

    const player = playerRef.current
    if (!player || !('IntersectionObserver' in window)) {
      setIsNearViewport(true)
      return undefined
    }

    const observer = new IntersectionObserver(
      ([entry]) => setIsNearViewport(entry.isIntersecting),
      {
        rootMargin: '240px 0px',
        threshold: 0.01,
      },
    )

    observer.observe(player)
    return () => observer.disconnect()
  }, [isMobile])

  useEffect(() => {
    if (!isMobile) return

    const video = videoRef.current
    const posterVideo = posterVideoRef.current

    if (!isNearViewport) {
      if (video) video.pause()
      if (posterVideo) posterVideo.pause()
      return
    }

    if (autoLoop) playSilently(video)
    if (!hasPlayed) playSilently(posterVideo)
  }, [autoLoop, hasPlayed, isMobile, isNearViewport])

  const handleLoadedMetadata = () => {
    if (lockAspectToPoster) return

    const video = videoRef.current
    if (!video?.videoWidth || !video?.videoHeight) return
    setAspectRatio((video.videoHeight / video.videoWidth) * 100)
  }

  const handleVideoReady = () => {
    setLoadError(false)
    setVideoReady(true)

    if (autoLoop && (!isMobile || isNearViewport)) {
      playSilently(videoRef.current)
    }
  }

  const playVideo = async () => {
    const video = videoRef.current
    if (!video) return

    if (posterVideoRef.current) posterVideoRef.current.pause()

    setLoadError(false)
    setHasPlayed(true)
    setPlaying(true)

    video.controls = false
    video.muted = false
    video.preload = 'auto'
    video.playsInline = true

    if (video.readyState < 2) video.load()

    try {
      await video.play()
    } catch (e) {
      setPlaying(false)
      setLoadError(true)
    }
  }

  const pauseVideo = () => {
    const video = videoRef.current
    if (!video) return
    video.pause()
    setPlaying(false)
  }

  const togglePlayback = () => {
    if (playing) {
      pauseVideo()
    } else {
      playVideo()
    }
  }

  const handleTimeUpdate = () => {
    const video = videoRef.current
    if (!video?.duration) return
    setPlayed(video.currentTime / video.duration)
  }

  const handleSeekChange = (e) => {
    const value = parseFloat(e.target.value)
    const video = videoRef.current
    setPlayed(value)
    if (video?.duration) {
      video.currentTime = value * video.duration
    }
  }

  const handleFullscreen = () => {
    const video = videoRef.current
    if (!video) return
    if (video.requestFullscreen) video.requestFullscreen()
    else if (video.webkitRequestFullscreen) video.webkitRequestFullscreen()
  }

  const handleVideoError = () => {
    setPlaying(false)

    if (!autoLoop) {
      setLoadError(true)
      return
    }

    window.setTimeout(() => {
      setRetryToken((value) => value + 1)
    }, 500)
  }

  const handlePosterVideoReady = (video) => {
    setPosterVideoReady(true)

    if (!hasPlayed && (!isMobile || isNearViewport)) {
      playSilently(video)
    }
  }

  const renderVimeoEmbed = () => (
    <StyledVideoPlayer
      ref={playerRef}
      className="video-player has-played-true is-mobile-false"
      aspectRatio={fallbackAspectRatio}
      playerTheme={playerTheme}
    >
      <div className="video-frame">
        <iframe
          src={vimeoUrl}
          title="Vimeo video"
          loading="lazy"
          allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
          allowFullScreen
        />
      </div>
    </StyledVideoPlayer>
  )

  const renderCustomControls = () => (
    <>
      <button
        className={`player-play-pause is-playing-${playing}`}
        onClick={togglePlayback}
        type="button"
        aria-label={playing ? 'Pause' : 'Play'}
      >
        Play/Pause
      </button>

      <button
        className="player-fullscreen"
        onClick={handleFullscreen}
        type="button"
        aria-label="Fullscreen"
      >
        Fullscreen
      </button>

      <div className="player-progress">
        <input
          aria-label="Video progress"
          type="range"
          min={0}
          max={0.999999}
          step="any"
          value={played}
          onChange={handleSeekChange}
          style={{
            background: `linear-gradient(to right, ${playerTheme} 0%, ${playerTheme} ${
              played * 100
            }%, #fff ${played * 100}%, #fff 100%)`,
          }}
        />
      </div>
    </>
  )

  if (effectiveVideoUrl) {
    const shouldPlayAmbientVideo = !isMobile || isNearViewport

    return (
      <StyledVideoPlayer
        ref={playerRef}
        className={`video-player has-played-${hasPlayed} is-loop-${autoLoop} is-mobile-${isMobile}`}
        aspectRatio={aspectRatio}
        playerTheme={playerTheme}
      >
        <div className="video-frame">
          <video
            key={`${effectiveVideoUrl}-${retryToken}`}
            ref={videoRef}
            className={`primary-video${videoReady ? ' is-ready' : ''}${
              autoLoop ? ' is-looping-video' : ''
            }${!autoLoop && !hasPlayed ? ' is-waiting-for-play' : ''}`}
            src={effectiveVideoUrl}
            poster={posterUrl}
            controls={false}
            defaultMuted={autoLoop}
            muted={autoLoop}
            loop={loop}
            autoPlay={autoLoop && shouldPlayAmbientVideo}
            playsInline
            preload={
              autoLoop || hasPlayed
                ? 'auto'
                : isMobile
                ? 'none'
                : 'metadata'
            }
            onLoadedMetadata={handleLoadedMetadata}
            onLoadedData={handleVideoReady}
            onCanPlay={handleVideoReady}
            onError={handleVideoError}
            onPlay={() => {
              setLoadError(false)
              setHasPlayed(true)
              setPlaying(true)
            }}
            onPause={() => setPlaying(false)}
            onEnded={() => setPlaying(false)}
            onTimeUpdate={handleTimeUpdate}
          />

          {!autoLoop && !hasPlayed && (
            <>
              <div className="poster-wrapper">
                {poster && !posterVideoReady && (
                  <div className="poster-image">
                    <BackgroundImage url={posterUrl} />
                  </div>
                )}

                {posterVideoUrl && !isMobile && (
                  <video
                    ref={posterVideoRef}
                    className={`poster-video${posterVideoReady ? ' is-ready' : ''}`}
                    src={posterVideoUrl}
                    defaultMuted
                    muted
                    loop
                    autoPlay={shouldPlayAmbientVideo}
                    playsInline
                    preload={shouldPlayAmbientVideo ? 'auto' : 'none'}
                    onCanPlay={(e) => handlePosterVideoReady(e.currentTarget)}
                    onLoadedData={(e) => handlePosterVideoReady(e.currentTarget)}
                    onPlaying={(e) => handlePosterVideoReady(e.currentTarget)}
                    onPause={(e) => {
                      if (!hasPlayed && shouldPlayAmbientVideo) {
                        playSilently(e.currentTarget)
                      }
                    }}
                    onEnded={(e) => {
                      if (shouldPlayAmbientVideo) playSilently(e.currentTarget)
                    }}
                  />
                )}
              </div>

              <div className="player-overlay" onClick={playVideo}>
                <button
                  className="player-play-icon"
                  type="button"
                  aria-label={loadError ? 'Retry video' : 'Play video'}
                >
                  {loadError ? 'Retry' : 'Play Icon'}
                </button>
              </div>
            </>
          )}

          {!autoLoop && hasPlayed && renderCustomControls()}

          {autoLoop && poster && (
            <div className="poster-wrapper is-looping-underlay">
              <div className="poster-image">
                <BackgroundImage url={posterUrl} />
              </div>
            </div>
          )}
        </div>
      </StyledVideoPlayer>
    )
  }

  if (!vimeoUrl) return null

  return renderVimeoEmbed()
}

export default VideoPlayer
